import React from 'react';
import { propertyLines } from './merge';

/**
 * Which one of an owner's properties a letter names.
 *
 * Not a native <select>: it gave no sign it could be opened, and it flattens an
 * option to one truncated line — while an owner's properties often differ only
 * in the address, so the part that tells them apart is what gets cut. Each
 * option here leads with that line and carries the city and name underneath.
 *
 * The menu is positioned against the viewport because one of these lives inside
 * the recipients list, which scrolls and would otherwise clip it.
 */
export default function PropertyPicker({ properties, value, onChange, ariaLabel }) {
    const [open, setOpen] = React.useState(false);
    const [box, setBox] = React.useState(null);
    const buttonRef = React.useRef(null);
    const rootRef = React.useRef(null);

    const chosen = properties.filter((property) => String(property.id) === String(value))[0] || properties[0];

    // Rows near the bottom of the window would otherwise drop their menu off
    // the screen, so those open upward instead.
    const place = () => {
        const rect = buttonRef.current.getBoundingClientRect();
        const below = window.innerHeight - rect.bottom;
        const anchor = below < 200 && rect.top > below
            ? { bottom: window.innerHeight - rect.top + 5 }
            : { top: rect.bottom + 5 };

        setBox({ ...anchor, left: rect.left, width: rect.width });
    };

    React.useEffect(() => {
        if (!open) return undefined;

        // React 16 delegates its own events to document, so stopPropagation in
        // a handler cannot hold off a native listener also on document — it
        // would close the menu before the click reached the option. Asking
        // whether the event came from inside works whatever React does.
        const inside = (event) => rootRef.current && rootRef.current.contains(event.target);
        const closeOutside = (event) => { if (!inside(event)) setOpen(false); };
        const onKey = (event) => { if (event.key === 'Escape') setOpen(false); };
        const onResize = () => setOpen(false);

        // A scroll anywhere else leaves the menu behind the button it belongs to.
        window.addEventListener('scroll', closeOutside, true);
        window.addEventListener('resize', onResize);
        document.addEventListener('mousedown', closeOutside);
        document.addEventListener('keydown', onKey);

        return () => {
            window.removeEventListener('scroll', closeOutside, true);
            window.removeEventListener('resize', onResize);
            document.removeEventListener('mousedown', closeOutside);
            document.removeEventListener('keydown', onKey);
        };
    }, [open]);

    const toggle = () => {
        if (!open) place();
        setOpen(!open);
    };

    const [label, sub] = propertyLines(chosen || {});

    return (
        <div className="mm-pick" ref={rootRef}>
            <button
                type="button"
                ref={buttonRef}
                className={'mm-pick__button' + (open ? ' is-open' : '')}
                aria-haspopup="listbox"
                aria-expanded={open}
                aria-label={ariaLabel}
                onClick={toggle}
            >
                <span className="mm-pick__value">
                    {label}
                    {sub ? <span className="mm-pick__sub">{sub}</span> : null}
                </span>
                <svg className={'mm-pick__caret' + (open ? ' is-open' : '')} width="11" height="11"
                    viewBox="0 0 11 11" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                    <path d="M3 4.3 L5.5 6.8 L8 4.3"/>
                </svg>
            </button>

            {open && box ? (
                <div className="mm-pick__menu" role="listbox" style={box}>
                    {properties.map((property) => {
                        const [primary, secondary] = propertyLines(property);
                        const on = chosen && String(property.id) === String(chosen.id);

                        return (
                            <button
                                key={property.id}
                                type="button"
                                role="option"
                                aria-selected={on}
                                className={'mm-pick__item' + (on ? ' is-on' : '')}
                                onClick={() => { onChange(String(property.id)); setOpen(false); }}
                            >
                                <span className="mm-pick__text">
                                    <span className="mm-pick__primary">{primary || 'Property ' + property.id}</span>
                                    {secondary ? <span className="mm-pick__secondary">{secondary}</span> : null}
                                </span>
                                {on ? (
                                    <svg className="mm-pick__check" width="12" height="12" viewBox="0 0 12 12"
                                        fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true">
                                        <path d="M2.4 6.3 L4.8 8.7 L9.6 3.5"/>
                                    </svg>
                                ) : null}
                            </button>
                        );
                    })}
                </div>
            ) : null}
        </div>
    );
}
